import React, { useState, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { Box, Typography, Select, MenuItem, FormControl, CircularProgress, Button } from "@mui/material";
import dayjs from "dayjs";
import donationApi from "../../../apis/donationApi";
import { ApexOptions } from "apexcharts";
import DatePicker from "../../DatePicker";

interface CashFlowChartProps {
  baseFilter?: any;
}

const CashFlowChart: React.FC<CashFlowChartProps> = ({ baseFilter }) => {
  const [filterType, setFilterType] = useState<"week" | "month" | "custom">("month");
  const [customFrom, setCustomFrom] = useState<any>(null);
  const [customTo, setCustomTo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<{
    categories: string[];
    shippingPrices: number[];
    zeroWastePrices: number[];
    donateAmounts: number[];
    buyProductsAmounts: number[];
  }>({
    categories: [],
    shippingPrices: [],
    zeroWastePrices: [],
    donateAmounts: [],
    buyProductsAmounts: [],
  });
  const [totals, setTotals] = useState({ shipping: 0, zeroWaste: 0, donate: 0, buyProducts: 0 });

  useEffect(() => {
    if (filterType !== "custom") {
      fetchData();
    }
  }, [filterType, baseFilter]);

  const handleApplyCustom = () => {
    if (!customFrom || !customTo) return;
    fetchData();
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const now = dayjs();
      let fromDate, toDate;
      if (filterType === "week") {
        fromDate = now.startOf("week").add(1, "day"); // Monday
        toDate = now.endOf("week").add(1, "day");
      } else if (filterType === "month") {
        fromDate = now.startOf("month");
        toDate = now.endOf("month");
      } else {
        fromDate = dayjs(customFrom).startOf("day");
        toDate = dayjs(customTo).endOf("day");
      }

      // We need to fetch all orders in this period to aggregate
      // Assuming limit: 2000 is enough for a month
      const req = {
        filter: {
          ...(baseFilter || {}),
          createdAts: {
            from: fromDate.format("YYYY-MM-DDTHH:mm:ssZ"),
            to: toDate.format("YYYY-MM-DDTHH:mm:ssZ"),
          }
        },
        paging: { limit: 2000, page: 1 }
      };

      const res = await donationApi.getDonations(req);
      if (res?.data?.code === 0) {
        const donations = res.data.data.userDonations || [];
        processChartData(donations, fromDate, toDate, filterType);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (donations: any[], fromDate: dayjs.Dayjs, toDate: dayjs.Dayjs, type: "week" | "month" | "custom") => {
    // Group by Date
    const grouped: Record<string, { shipping: number, zeroWaste: number, donate: number, buyProducts: number }> = {};
    
    // Initialize days
    let current = fromDate.clone();
    const categories: string[] = [];
    while (current.isBefore(toDate) || current.isSame(toDate, "day")) {
      const dateStr = current.format("DD/MM");
      categories.push(dateStr);
      grouped[dateStr] = { shipping: 0, zeroWaste: 0, donate: 0, buyProducts: 0 };
      current = current.add(1, "day");
    }

    let totShip = 0, totZero = 0, totDonate = 0, totBuy = 0;

    donations.forEach(order => {
      const dateStr = dayjs(order.createdAt || order.userDonationTime).format("DD/MM");
      if (!grouped[dateStr]) return;

      const shipping = order.shippingPrice || 0;
      let zeroWaste = order.zeroWastePrice || 0; 
      let donate = 0;
      let buy = 0;

      if (order.services && order.services.length > 0) {
        order.services.forEach((svc: any) => {
          if (svc.donationAmount) donate += svc.donationAmount;
          if (svc.buyProducts && svc.buyProducts.length > 0) {
            svc.buyProducts.forEach((prod: any) => {
              buy += (prod.price || 0) * (prod.quantity || 1);
            });
          }
        });
      }

      grouped[dateStr].shipping += shipping;
      grouped[dateStr].zeroWaste += zeroWaste;
      grouped[dateStr].donate += donate;
      grouped[dateStr].buyProducts += buy;

      totShip += shipping;
      totZero += zeroWaste;
      totDonate += donate;
      totBuy += buy;
    });

    setTotals({ shipping: totShip, zeroWaste: totZero, donate: totDonate, buyProducts: totBuy });

    const shippingPrices = categories.map(c => grouped[c].shipping);
    const zeroWastePrices = categories.map(c => grouped[c].zeroWaste);
    const donateAmounts = categories.map(c => grouped[c].donate);
    const buyProductsAmounts = categories.map(c => grouped[c].buyProducts);

    setChartData({
      categories,
      shippingPrices,
      zeroWastePrices,
      donateAmounts,
      buyProductsAmounts
    });
  };

  const series = [
    { name: "Cước vận chuyển", data: chartData.shippingPrices },
    { name: "Phí xử lý", data: chartData.zeroWastePrices },
    { name: "Ủng hộ tái chế", data: chartData.donateAmounts },
    { name: "Mua SP tái chế", data: chartData.buyProductsAmounts },
  ];

  const options: ApexOptions = {
    chart: { type: "bar", stacked: true, toolbar: { show: false } },
    plotOptions: { bar: { horizontal: false, borderRadius: 2, columnWidth: "50%" } },
    xaxis: { categories: chartData.categories },
    yaxis: { labels: { formatter: (val) => `${val.toLocaleString()} đ` } },
    fill: { opacity: 1 },
    colors: ["#2196f3", "#ff9800", "#4caf50", "#9c27b0"],
    legend: { position: "top", horizontalAlign: "center" },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} đ` } },
    dataLabels: { enabled: false }
  };

  return (
    <Box sx={{ p: 3, mb: 3, bgcolor: "#fff", borderRadius: 2, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
          Báo cáo dòng tiền
        </Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {filterType === "custom" && (
            <>
              <DatePicker
                label="Từ ngày"
                value={customFrom}
                setValue={setCustomFrom}
              />
              <DatePicker
                label="Đến ngày"
                value={customTo}
                setValue={setCustomTo}
              />
              <Button variant="contained" color="primary" onClick={handleApplyCustom} disabled={!customFrom || !customTo}>
                Áp dụng
              </Button>
            </>
          )}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select value={filterType} onChange={(e) => setFilterType(e.target.value as "week" | "month" | "custom")}>
              <MenuItem value="week">Tuần này</MenuItem>
              <MenuItem value="month">Tháng này</MenuItem>
              <MenuItem value="custom">Tùy chỉnh</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 3, mb: 2, flexWrap: "wrap" }}>
            <Box sx={{ px: 2, py: 1, bgcolor: "#e3f2fd", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">Cước vận chuyển</Typography>
              <Typography variant="h6" color="#1976d2" fontWeight="bold">{totals.shipping.toLocaleString()}đ</Typography>
            </Box>
            <Box sx={{ px: 2, py: 1, bgcolor: "#fff3e0", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">Phí xử lý</Typography>
              <Typography variant="h6" color="#f57c00" fontWeight="bold">{totals.zeroWaste.toLocaleString()}đ</Typography>
            </Box>
            <Box sx={{ px: 2, py: 1, bgcolor: "#e8f5e9", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">Ủng hộ tái chế</Typography>
              <Typography variant="h6" color="#388e3c" fontWeight="bold">{totals.donate.toLocaleString()}đ</Typography>
            </Box>
            <Box sx={{ px: 2, py: 1, bgcolor: "#f3e5f5", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">Mua SP tái chế</Typography>
              <Typography variant="h6" color="#7b1fa2" fontWeight="bold">{totals.buyProducts.toLocaleString()}đ</Typography>
            </Box>
          </Box>
          <ReactApexChart options={options} series={series} type="bar" height={300} />
        </>
      )}
    </Box>
  );
};

export default CashFlowChart;
